import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TableSortLabel
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Refresh } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import axios from '../../../resources/axios';

interface ScooterBatteryData {
  scooter: string;
  trackerBattery: number;
  city: string;
}

type OrderDirection = 'asc' | 'desc';

const FullBatteryTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [data, setData] = useState<ScooterBatteryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderBy, setOrderBy] = useState<keyof ScooterBatteryData>('scooter');
  const [order, setOrder] = useState<OrderDirection>('asc');

  const handleFetchData = async () => {
    setLoading(true);
    setError('');
    setData([]);

    try {
      const response = await axios.get('/scooters/tracker_battery_excel', {
        responseType: 'arraybuffer'
      });

      const workbook = XLSX.read(response.data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet);

      setData(jsonData as ScooterBatteryData[]);

      if (jsonData.length === 0) {
        setError(t('stats.noFullBatteryScooters'));
      }
    } catch (err: any) {
      console.error('Error fetching battery data:', err);
      setError(err.response?.data?.message || t('stats.dataLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (property: keyof ScooterBatteryData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];
    
    if (aValue < bValue) {
      return order === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return order === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : theme.palette.background.paper,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 2
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {t('stats.fullBatteryTitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0',
              mb: 2
            }}
          >
            {t('stats.fullBatteryDesc')}
          </Typography>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
            onClick={handleFetchData}
            disabled={loading}
            sx={{
              backgroundColor: '#5073F8',
              '&:hover': { backgroundColor: '#3d5dd6' }
            }}
          >
            {loading ? t('stats.loading') : t('stats.getLatestData')}
          </Button>
        </Box>

        {error && !data.length && (
          <Alert severity={error.includes('no scooters') || error.includes('немає') ? 'info' : 'error'} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {data.length > 0 && (
          <>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: '#4CAF50'
              }}
            >
              {t('stats.fullBatteryCount', { count: data.length })}
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 60 }}>№</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'scooter'}
                        direction={orderBy === 'scooter' ? order : 'asc'}
                        onClick={() => handleSort('scooter')}
                        sx={{
                          '&.Mui-active': {
                            color: '#5073F8',
                            '& .MuiTableSortLabel-icon': {
                              color: '#5073F8 !important'
                            }
                          }
                        }}
                      >
                        {t('stats.scooterNumber')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'city'}
                        direction={orderBy === 'city' ? order : 'asc'}
                        onClick={() => handleSort('city')}
                        sx={{
                          '&.Mui-active': {
                            color: '#5073F8',
                            '& .MuiTableSortLabel-icon': {
                              color: '#5073F8 !important'
                            }
                          }
                        }}
                      >
                        {t('stats.cityColumn')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={orderBy === 'trackerBattery'}
                        direction={orderBy === 'trackerBattery' ? order : 'asc'}
                        onClick={() => handleSort('trackerBattery')}
                        sx={{
                          '&.Mui-active': {
                            color: '#5073F8',
                            '& .MuiTableSortLabel-icon': {
                              color: '#5073F8 !important'
                            }
                          }
                        }}
                      >
                        {t('stats.chargeColumn')}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? 'rgba(78, 205, 196, 0.25)' : 'rgba(78, 205, 196, 0.1)'
                        }
                      }}
                    >
                      <TableCell sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.scooter}</TableCell>
                      <TableCell>{row.city}</TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-block',
                            backgroundColor: '#4CAF50',
                            color: '#FFFFFF',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontWeight: 600
                          }}
                        >
                          {row.trackerBattery}%
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default FullBatteryTab;
